import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/* Camera icon for "snap your meals" */
const MealIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4A2E1F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);

/* Poop swirl icon */
const PoopIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="#8B7355" stroke="none">
    <ellipse cx="12" cy="20" rx="7" ry="3"/>
    <ellipse cx="12" cy="16" rx="5.5" ry="2.5"/>
    <ellipse cx="12" cy="12.5" rx="4" ry="2"/>
    <path d="M13 5c0 0 2 1 2 3s-2 3-2 3" fill="none" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4A2E1F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

function Landing() {
  const navigate = useNavigate();
  useEffect(() => {
    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isMobile && !localStorage.getItem('onboarding_complete')) {
      navigate('/onboarding', { replace: true });
    }
  }, [navigate]);

  return (
    <div className="landing-page">
      {/* ── Hero ── */}
      <section className="landing-hero">
        <div className="landing-logo">
          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '32px', fontWeight: '800', color: '#4A2E1F', letterSpacing: '-0.02em' }}>Tract</span>
        </div>
        <h1 className="landing-hero-title">
          Stop guessing which<br />
          <span className="landing-hero-accent">foods wreck your gut.</span>
        </h1>
        <p className="landing-hero-sub">
          Snap a photo of your meal. Log how you feel later.
          AI finds the patterns — no spreadsheets, no food diaries, no FODMAP charts.
        </p>
        <Link to="/register" className="btn btn-primary landing-cta">
          Get Started Free
        </Link>
        <p className="landing-hero-signin">
          Already have an account?&nbsp;
          <Link to="/login" className="landing-link">Sign in</Link>
        </p>
      </section>

      {/* ── Pain Point ── */}
      <section className="landing-section">
        <h2 className="landing-section-title">Sound familiar?</h2>
        <div className="landing-pain-points">
          <div className="landing-pain-card">
            <p className="landing-pain-quote">"I've tried elimination diets three times. I still don't know what's triggering my flares."</p>
          </div>
          <div className="landing-pain-card">
            <p className="landing-pain-quote">"My gastroenterologist told me to keep a food diary. I lasted four days."</p>
          </div>
          <div className="landing-pain-card">
            <p className="landing-pain-quote">"Some days dairy is fine. Some days it destroys me. I can't find the pattern."</p>
          </div>
        </div>
        <p className="landing-pain-followup">
          IBS affects 1 in 10 people. Most never find their triggers because tracking is too hard.
          <br />Tract makes it effortless.
        </p>
      </section>

      {/* ── How It Works ── */}
      <section className="landing-section">
        <h2 className="landing-section-title">How it works</h2>
        <div className="landing-steps">
          <div className="landing-step">
            <span className="landing-step-num">1</span>
            <span className="landing-step-icon"><MealIcon /></span>
            <h3>Snap your meals</h3>
            <p>Just take a photo. AI identifies every food, ingredient, and hidden trigger — even ones you wouldn't think to log.</p>
          </div>
          <div className="landing-step">
            <span className="landing-step-num">2</span>
            <span className="landing-step-icon"><PoopIcon /></span>
            <h3>Log how you feel</h3>
            <p>One tap. Pick your Bristol type. Takes 5 seconds. That's the whole commitment.</p>
          </div>
          <div className="landing-step">
            <span className="landing-step-num">3</span>
            <span className="landing-step-icon"><ChartIcon /></span>
            <h3>Get your triggers</h3>
            <p>After a week of data, AI surfaces the foods most correlated with your symptoms — with confidence scores.</p>
          </div>
        </div>
      </section>

      {/* ── Example Report Cards ── */}
      <section className="landing-section">
        <h2 className="landing-section-title">What you'll discover</h2>
        <p className="landing-section-sub">Real example from a user after 2 weeks of logging:</p>
        <div className="landing-report-cards">
          <div className="landing-insight-card">
            <p className="landing-insight-eyebrow">Summary</p>
            <p className="landing-insight-summary" style={{ marginBottom: 0 }}>
              Dairy and garlic show the strongest correlation with your symptoms.
              Meals containing cream or yogurt were followed by issues 75% of the time.
            </p>
          </div>
          <div className="landing-insight-card">
            <p className="landing-insight-eyebrow">Potential Triggers</p>
            <div className="landing-insight-row">
              <span className="landing-insight-label">Dairy</span>
              <span className="landing-insight-badge landing-insight-badge--red">82% likely</span>
            </div>
            <p className="landing-insight-reason">Present in 6 of 8 meals before symptoms</p>
            <div className="landing-insight-row">
              <span className="landing-insight-label">Garlic</span>
              <span className="landing-insight-badge landing-insight-badge--yellow">65% likely</span>
            </div>
            <p className="landing-insight-reason">Eaten 10 times, suspect in 6 cases</p>
            <div className="landing-insight-row">
              <span className="landing-insight-label">Wheat / Gluten</span>
              <span className="landing-insight-badge landing-insight-badge--green">48% likely</span>
            </div>
            <p className="landing-insight-reason">May be coincidental with dairy</p>
          </div>
          <div className="landing-insight-card">
            <p className="landing-insight-eyebrow">Safe Foods</p>
            <div className="landing-insight-safe">
              <span className="landing-insight-safe-icon">&#10003;</span>
              <span><strong>Rice</strong> — eaten 7 times with no issues</span>
            </div>
            <div className="landing-insight-safe">
              <span className="landing-insight-safe-icon">&#10003;</span>
              <span><strong>Chicken</strong> — eaten 5 times, only 1 loose stool</span>
            </div>
            <div className="landing-insight-safe">
              <span className="landing-insight-safe-icon">&#10003;</span>
              <span><strong>Oat milk</strong> — dairy substitute with no issues</span>
            </div>
          </div>
          <div className="landing-insight-card">
            <p className="landing-insight-eyebrow">Timing</p>
            <p className="landing-insight-summary" style={{ marginBottom: 0 }}>
              Most symptoms appear 8–14 hours after eating trigger foods.
              Garlic-heavy meals tend to cause issues slightly faster (6–10 hours).
            </p>
          </div>
          <div className="landing-insight-card">
            <p className="landing-insight-eyebrow">Next Steps</p>
            <ul className="landing-insight-steps">
              <li>Try eliminating dairy for 2 weeks and note any changes</li>
              <li>When eating garlic, avoid combining with dairy</li>
              <li>Keep logging — 2 more weeks of data will improve accuracy</li>
            </ul>
          </div>
          <p className="landing-insight-disclaimer" style={{ textAlign: 'center' }}>
            Based on your logged data — not medical advice.
          </p>
        </div>
      </section>

      {/* ── Why It Works ── */}
      <section className="landing-section">
        <h2 className="landing-section-title">Why this works when diaries don't</h2>
        <div className="landing-why-grid">
          <div className="landing-why-card">
            <h3>AI reads your food photos</h3>
            <p>You don't type "caesar salad with parmesan, croutons, romaine, anchovy dressing." You take a photo. AI extracts every ingredient, including the hidden ones — garlic in the dressing, dairy in the croutons.</p>
          </div>
          <div className="landing-why-card">
            <h3>It catches what you'd miss</h3>
            <p>Your trigger might not be "pizza." It might be the specific combination of wheat + dairy + tomato. The correlation engine looks at 6–36 hour windows across every food you've eaten.</p>
          </div>
          <div className="landing-why-card">
            <h3>5 seconds, not 5 minutes</h3>
            <p>Food diaries fail because they're tedious. This is a photo and a tap. Low enough friction that you'll actually do it every meal.</p>
          </div>
        </div>
      </section>

      {/* ── Social Proof ── */}
      <section className="landing-section">
        <h2 className="landing-section-title">Built for people with IBS</h2>
        <p className="landing-pain-followup">
          Whether it's IBS, SIBO, food sensitivities, or just a gut that hates you sometimes —
          Tract helps you find the pattern. No FODMAP memorization required.
        </p>
        <div className="landing-stats">
          <div className="landing-stat">
            <span className="landing-stat-num">10 sec</span>
            <span className="landing-stat-label">to log a meal</span>
          </div>
          <div className="landing-stat">
            <span className="landing-stat-num">1 week</span>
            <span className="landing-stat-label">to first insights</span>
          </div>
          <div className="landing-stat">
            <span className="landing-stat-num">100%</span>
            <span className="landing-stat-label">free, no ads</span>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="landing-section landing-bottom-cta">
        <h2 className="landing-section-title">Your gut is trying to tell you something.</h2>
        <p className="landing-hero-sub">Start logging today. It takes 30 seconds and zero commitment.</p>
        <Link to="/register" className="btn btn-primary landing-cta">
          Get Started Free
        </Link>
        <p className="landing-hero-signin">
          Already have an account?&nbsp;
          <Link to="/login" className="landing-link">Sign in</Link>
        </p>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <p className="landing-footer-brand">Tract</p>
      </footer>
    </div>
  );
}

export default Landing;
